import React, { forwardRef, useRef, useMemo } from 'react'
import { AgGridAdapter } from './adapters'
import { TableSkeleton, TableEmpty } from './components'
import { useTableState, useTableRef } from './hooks'
import {
    transformColumnsToAgGrid,
    transformLegacyColumns,
    isLegacyFormat,
    transformLegacyRows,
} from './handlers'
import styles from './ScoopTable.module.scss'

/**
 * ScoopTable - Unified table component built on AG Grid
 *
 * Supports two data formats:
 * 1. Legacy format: data={{ columns: [{columnName, columnType}], rows: [[]] }}
 * 2. New format: columns={[{field, header, render, ...}]} rows={[{...}]}
 *
 * @param {Object} props - Component props
 *
 * @param {Object} [props.data] - Legacy data format with columns and rows arrays
 * @param {Array<{columnName: string, columnType: string}>} [props.data.columns] - Column definitions
 * @param {Array<Array>} [props.data.rows] - Row data as 2D array
 *
 * @param {Array<Object>} [props.columns] - New format column definitions
 * @param {string} props.columns[].field - Field name (required)
 * @param {string} [props.columns[].header] - Header display text
 * @param {Function} [props.columns[].render] - Custom cell renderer (value, row) => ReactNode
 * @param {number} [props.columns[].width] - Column width in pixels
 * @param {number} [props.columns[].minWidth] - Minimum column width
 * @param {number} [props.columns[].maxWidth] - Maximum column width
 * @param {string} [props.columns[].align] - Text alignment: 'left' | 'center' | 'right'
 * @param {boolean} [props.columns[].sortable] - Override default sortable setting
 *
 * @param {Array<Object>} [props.rows] - New format row data as array of objects
 *
 * @param {Object} [props.sorting={}] - Sorting configuration
 * @param {boolean} [props.sorting.enabled] - Enable column sorting
 * @param {string} [props.sorting.defaultField] - Default sort field
 * @param {string} [props.sorting.defaultDirection] - Default sort direction: 'asc' | 'desc'
 * @param {Function} [props.sorting.onSortChange] - Callback when sort changes (field, direction) => void
 *
 * @param {Object} [props.pagination={}] - Pagination configuration
 * @param {boolean} [props.pagination.enabled] - Enable pagination
 * @param {number} [props.pagination.pageSize] - Rows per page
 * @param {Array<number>} [props.pagination.pageSizeOptions] - Available page size options
 * @param {boolean} [props.pagination.customPagination] - Hide default pagination panel
 *
 * @param {Object} [props.selection={}] - Row selection configuration
 * @param {string} [props.selection.mode] - Selection mode: 'none' | 'single' | 'multiple'
 * @param {Function} [props.selection.isRowSelectable] - Callback to determine if row is selectable (row) => boolean
 * @param {Function} [props.selection.onSelectionChange] - Callback when selection changes (selectedRows) => void
 *
 * @param {boolean} [props.resizable=true] - Enable column resizing
 * @param {boolean} [props.reorderable=true] - Enable column reordering via drag and drop
 *
 * @param {boolean} [props.loading=false] - Show loading skeleton
 * @param {string} [props.emptyMessage='No data available'] - Message when no data
 * @param {React.Component} [props.emptyComponent] - Custom empty state component
 *
 * @param {number|string} [props.height] - Container height (e.g., 400, '100%')
 * @param {number} [props.rowHeight=40] - Row height in pixels
 * @param {number} [props.headerHeight=44] - Header height in pixels
 * @param {string} [props.domLayout='autoHeight'] - AG Grid layout: 'autoHeight' | 'normal' | 'print'
 * @param {number} [props.columnWidth] - Default width for all columns (can be overridden per column)
 *
 * @param {Function} [props.onRowClick] - Row click handler (rowData, event) => void
 * @param {Function} [props.onRowDoubleClick] - Row double click handler (rowData, event) => void
 * @param {Function} [props.onGridReady] - AG Grid ready callback (event) => void
 * @param {Function} [props.onSelectionChanged] - Selection changed callback (event) => void
 *
 * @param {string} [props.className] - Additional CSS class for container
 * @param {Object} [props.style] - Additional inline styles for container
 *
 * @param {React.Ref} [ref] - Ref to access table methods: getSelectedRows(), selectAll(), deselectAll(), exportToCsv()
 *
 * @example
 * // New format
 * <ScoopTable
 *   columns={[
 *     { field: 'name', header: 'Name' },
 *     { field: 'email', header: 'Email' },
 *     { field: 'status', header: 'Status', render: (val) => <Badge>{val}</Badge> }
 *   ]}
 *   rows={[{ name: 'John', email: 'john@example.com', status: 'active' }]}
 *   sorting={{ enabled: true }}
 *   pagination={{ enabled: true, pageSize: 10 }}
 * />
 *
 * @example
 * // Legacy format
 * <ScoopTable
 *   data={{
 *     columns: [{ columnName: 'Name', columnType: 'String' }],
 *     rows: [['John'], ['Jane']]
 *   }}
 * />
 */
const ScoopTable = forwardRef(
    (
        {
            // Legacy format
            data,

            // New format - Data
            columns: propColumns,
            rows: propRows,

            // Features
            sorting = {},
            pagination = {},
            selection = {},
            resizable = true,
            reorderable = true,

            // State
            loading = false,
            emptyMessage = 'No data available',
            emptyComponent: EmptyComponent,

            // Sizing
            height,
            rowHeight = 40,
            headerHeight = 44,
            domLayout = 'autoHeight',
            columnWidth,

            // Events
            onRowClick,
            onRowDoubleClick,
            onGridReady,
            onSelectionChanged,

            // Styling
            className,
            style,

            // AG Grid pass-through props
            ...agGridProps
        },
        ref
    ) => {
        // Internal grid ref
        const gridRef = useRef(null)

        // Detect and transform data format
        const { columns, rows } = useMemo(() => {
            // Legacy format: data prop with columns and rows arrays
            if (data && isLegacyFormat(data)) {
                return {
                    columns: transformLegacyColumns(data.columns),
                    rows: transformLegacyRows(data.rows, data.columns),
                }
            }

            // New format: separate columns and rows props
            return {
                columns: propColumns || [],
                rows: propRows || [],
            }
        }, [data, propColumns, propRows])

        // Transform columns to AG-Grid format
        const agGridColumns = useMemo(() => {
            return transformColumnsToAgGrid(columns, { defaultWidth: columnWidth })
        }, [columns, columnWidth])

        // Table state management
        const tableState = useTableState({
            sorting,
            pagination,
            selection,
            rows,
        })

        // Expose methods via ref
        useTableRef(ref, gridRef, {
            rows,
            selectedRows: tableState.selectedRows,
            onSelectionChange: tableState.handleSelectionChange,
        })

        // Container class
        const containerClass = useMemo(() => {
            const classes = [styles.container]
            if (className) classes.push(className)
            return classes.join(' ')
        }, [className])

        // Container style
        const containerStyle = useMemo(() => {
            const baseStyle = {}
            if (height) baseStyle.height = height
            return { ...baseStyle, ...style }
        }, [height, style])

        // Loading state
        if (loading) {
            return (
                <div className={containerClass} style={containerStyle}>
                    <TableSkeleton
                        rows={pagination.pageSize || 5}
                        columns={columns.length || 4}
                        rowHeight={rowHeight}
                        headerHeight={headerHeight}
                    />
                </div>
            )
        }

        // Empty state
        if (!rows || rows.length === 0) {
            return (
                <div className={containerClass} style={containerStyle}>
                    {EmptyComponent ? <EmptyComponent /> : <TableEmpty message={emptyMessage} />}
                </div>
            )
        }

        // Render table
        return (
            <div className={containerClass} style={containerStyle}>
                <AgGridAdapter
                    gridRef={gridRef}
                    columns={agGridColumns}
                    rows={rows}
                    sorting={sorting}
                    pagination={pagination}
                    selection={selection}
                    resizable={resizable}
                    reorderable={reorderable}
                    rowHeight={rowHeight}
                    headerHeight={headerHeight}
                    domLayout={domLayout}
                    height={height}
                    onRowClick={onRowClick}
                    onRowDoubleClick={onRowDoubleClick}
                    onGridReady={onGridReady}
                    onSortChanged={tableState.handleAgGridSortChanged}
                    onSelectionChanged={
                        onSelectionChanged || tableState.handleAgGridSelectionChanged
                    }
                    {...agGridProps}
                />
            </div>
        )
    }
)

ScoopTable.displayName = 'ScoopTable'

export default ScoopTable
